"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ProductClientState, ThreeDFormState } from "@/zod/product.schema";
import { PreviewSVG, TrashCanSVG } from "@/components/ui/SVG";
import DotPulseLdr from "@/components/ui/DotPulseLdr";

interface EditThreeDPanelProps {
  isOpen: boolean;
  onModelPick: (modelUuid?: string) => void;
  onModelDelete: (modelUuid: string) => Promise<void>;
}

function EditThreeDPanel({
  isOpen,
  onModelPick,
  onModelDelete,
}: EditThreeDPanelProps) {
  const { watch, getValues, setValue } = useFormContext<ProductClientState>();
  const models = (watch("threeD") || []) as ThreeDFormState[];

  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const applyModelPatch = (uuid: string, patch: Partial<ThreeDFormState>) => {
    const currentModels = (getValues("threeD") || []) as ThreeDFormState[];
    const updatedModels = currentModels.map((model) =>
      model.uuid === uuid ? { ...model, ...patch } : model
    );

    setValue("threeD", updatedModels, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const handleTitleChange = (
    uuid: string,
    field: "title_en" | "title_fa",
    value: string
  ) => {
    applyModelPatch(uuid, { [field]: value } as Partial<ThreeDFormState>);
  };

  const handleNumericChange = (
    uuid: string,
    field: "exposure" | "shadow_intensity",
    value: string
  ) => {
    if (value === "") {
      applyModelPatch(uuid, { [field]: "" } as Partial<ThreeDFormState>);
      return;
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    applyModelPatch(uuid, { [field]: parsed } as Partial<ThreeDFormState>);
  };

  const handleDelete = async (uuid: string) => {
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[uuid];
      return next;
    });

    setDeletingUuid(uuid);
    try {
      await onModelDelete(uuid);
    } catch (error: any) {
      const message =
        error?.message && typeof error.message === "string"
          ? error.message
          : "Failed to delete 3D model.";
      setRowErrors((prev) => ({ ...prev, [uuid]: message }));
    } finally {
      setDeletingUuid(null);
    }
  };

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-6">
      <button
        type="button"
        onClick={() => onModelPick()}
        className="flex items-center gap-2 rounded-full border border-dashed border-slateCustom px-6 py-3 text-slateCustom transition hover:bg-slateCustom hover:text-white"
      >
        <span className="text-2xl leading-none">+</span>
        <span className="text-sm font-medium">Add 3D Model</span>
      </button>
    </div>
  );

  const renderList = () => (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onModelPick()}
          className="flex items-center gap-2 rounded-md border border-slateCustom px-3 py-1 text-sm font-medium text-slateCustom transition hover:bg-slateCustom hover:text-white"
        >
          <span className="text-lg leading-none">+</span>
          <span>Add 3D Model</span>
        </button>
      </div>
      {models.map((model) => {
        const viewHref = model.url
          ? `https://model-viewer-website.vercel.app/?${encodeURIComponent(
              model.url
            )}`
          : "#";
        const isDeleting = deletingUuid === model.uuid;
        const rowError = rowErrors[model.uuid];
        const exposureValue =
          model.exposure === null ||
          model.exposure === undefined ||
          model.exposure === ""
            ? ""
            : Number(model.exposure);
        const shadowIntensityValue =
          model.shadow_intensity === null ||
          model.shadow_intensity === undefined ||
          model.shadow_intensity === ""
            ? ""
            : Number(model.shadow_intensity);

        return (
          <div
            key={model.uuid}
            className="relative overflow-hidden rounded-md border border-gray-200 bg-white p-4 shadow-sm"
          >
            {isDeleting && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm text-sm font-medium text-slateCustom">
                Deleting...
              </div>
            )}
            <div className="flex flex-col gap-4 md:flex-row md:items-end">
              <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Title (EN)
                  </label>
                  <input
                    value={model.title_en ?? ""}
                    onChange={(event) =>
                      handleTitleChange(
                        model.uuid,
                        "title_en",
                        event.target.value
                      )
                    }
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-slateCustom focus:outline-none"
                    placeholder="Model title in English"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Title (FA)
                  </label>
                  <input
                    value={model.title_fa ?? ""}
                    onChange={(event) =>
                      handleTitleChange(
                        model.uuid,
                        "title_fa",
                        event.target.value
                      )
                    }
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-slateCustom focus:outline-none"
                    placeholder="عنوان مدل"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Exposure
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      value={exposureValue}
                      onChange={(event) =>
                        handleNumericChange(
                          model.uuid,
                          "exposure",
                          event.target.value
                        )
                      }
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-slateCustom focus:outline-none"
                      placeholder="Default: 1"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Shadow intensity
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={2}
                      step={0.05}
                      value={shadowIntensityValue}
                      onChange={(event) =>
                        handleNumericChange(
                          model.uuid,
                          "shadow_intensity",
                          event.target.value
                        )
                      }
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-slateCustom focus:outline-none"
                      placeholder="Default: 1"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 md:w-auto md:flex-row">
                <a
                  href={viewHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition ${
                    model.url
                      ? "bg-slateCustom text-white hover:bg-slate-700"
                      : "cursor-not-allowed bg-gray-200 text-gray-500"
                  }`}
                  aria-disabled={!model.url}
                >
                  <PreviewSVG className="w-4" svgColor="#fff" />
                </a>
                <button
                  type="button"
                  onClick={() => onModelPick(model.uuid)}
                  className="flex items-center justify-center rounded-md border border-slateCustom px-4 py-2 text-sm font-medium text-slateCustom transition hover:bg-slateCustom hover:text-white"
                >
                  Replace
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(model.uuid)}
                  className="flex items-center justify-center rounded-md bg-red-100 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-200"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <DotPulseLdr size={25} color="red" />
                  ) : (
                    <TrashCanSVG
                      className="w-5"
                      svgColor="var(--color-red-600)"
                    />
                  )}
                </button>
              </div>
            </div>
            {rowError && (
              <p className="mt-3 text-xs text-red-600" role="alert">
                {rowError}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          transition={{ duration: 0.2 }}
          style={{ overflow: "hidden" }}
          className="bg-grayCustom px-4"
        >
          <div className="py-4">
            {models.length === 0 ? renderEmptyState() : renderList()}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default EditThreeDPanel;
